import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, Lock, Calendar as CalendarIcon, Receipt, X, Loader2, Paperclip } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { useChit } from "@/context/ChitContext";
import { useAuth } from "@/context/AuthContext";
import { CATEGORII } from "@/lib/categories";
import {
  imparteEgal,
  ajusteazaRest,
  getSplits,
  formateazaRON,
} from "@/lib/settlement";

// Cele 3 moduri de împărțire a unei cheltuieli.
const MODURI = [
  { id: "egal", label: "Egal" },
  { id: "sume", label: "Sume" },
  { id: "procente", label: "Procente" },
];

// Valutele disponibile (RON gratuit; restul se convertesc în RON — Premium).
const VALUTE = ["RON", "EUR", "USD", "GBP"];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Drawer pentru adăugarea SAU editarea unei cheltuieli.
export function AddExpenseDrawer({ open, onOpenChange, group, expense }) {
  const { addExpense, updateExpense } = useChit();
  const { isPremium } = useAuth();
  const navigate = useNavigate();
  const esteEditare = Boolean(expense);

  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [paidBy, setPaidBy] = useState("");
  const [category, setCategory] = useState("altele");
  const [splitMode, setSplitMode] = useState("egal");
  const [selected, setSelected] = useState([]); // pt. modul „egal"
  const [sume, setSume] = useState({}); // pt. „sume": { memberId: "12.50" }
  const [procente, setProcente] = useState({}); // pt. „procente": { memberId: "50" }
  const [currency, setCurrency] = useState("RON");
  const [fx, setFx] = useState(null); // { rates:{EUR,USD,GBP}, date, source }
  const [expenseDate, setExpenseDate] = useState(new Date());
  const [showCal, setShowCal] = useState(false);
  const [receiptId, setReceiptId] = useState(null);
  const [receiptName, setReceiptName] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Aducem cursul valutar (o singură dată, pentru premium) când se deschide.
  useEffect(() => {
    if (!open || !isPremium || fx) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/fx/rates`, { withCredentials: true });
        setFx(r.data);
      } catch {
        // dacă nu vine cursul, rămâne doar RON
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isPremium]);

  // Când se deschide drawer-ul, pregătim valorile inițiale.
  useEffect(() => {
    if (!open) return;
    if (esteEditare) {
      const cur = expense.currency || "RON";
      setCurrency(cur);
      setAmount(
        cur !== "RON" && expense.originalAmount != null
          ? String(expense.originalAmount)
          : String(expense.amount)
      );
      setDescription(expense.description);
      setPaidBy(expense.paidBy);
      setCategory(expense.category || "altele");
      setSplitMode(expense.splitMode || "egal");
      const splits = getSplits(expense);
      setSelected(splits.map((s) => s.memberId));
      // Sumele „exacte" se afișează în moneda originală a cheltuielii (nu în RON),
      // convertite folosind cursul aplicat la crearea ei (amount / originalAmount).
      const impliedRate =
        cur !== "RON" && expense.originalAmount ? expense.amount / expense.originalAmount : 1;
      const s = {};
      const p = {};
      splits.forEach((x) => {
        const sumaAfisata = cur !== "RON" ? x.amount / impliedRate : x.amount;
        s[x.memberId] = String(Math.round(sumaAfisata * 100) / 100);
        p[x.memberId] = expense.amount
          ? String(Math.round((x.amount / expense.amount) * 100))
          : "";
      });
      setSume(s);
      setProcente(p);
      setExpenseDate(expense.createdAt ? new Date(expense.createdAt) : new Date());
      setShowCal(false);
      setReceiptId(expense.receiptId ?? null);
      setReceiptName(expense.receiptName ?? null);
    } else {
      setAmount("");
      setDescription("");
      setPaidBy(group.members[0]?.id || "");
      setCategory("altele");
      setSplitMode("egal");
      setSelected(group.members.map((m) => m.id)); // implicit: toți
      setSume({});
      setProcente({});
      setCurrency("RON");
      setExpenseDate(new Date());
      setShowCal(false);
      setReceiptId(null);
      setReceiptName(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const total = parseFloat(amount);
  const totalValid = Number.isFinite(total) && total >= 0.01;

  // Cursul pentru moneda aleasă (RON = 1). Totalul convertit în RON.
  const rate = currency === "RON" ? 1 : fx?.rates?.[currency] || null;
  const totalRON =
    currency === "RON"
      ? total
      : totalValid && rate
      ? Math.round(total * rate * 100) / 100
      : NaN;
  const totalRONValid = Number.isFinite(totalRON) && totalRON >= 0.01;

  // Mesaj de upsell pentru funcțiile Premium.
  const upsell = (mesaj) => {
    toast.error(mesaj, {
      action: { label: "Vezi Premium", onClick: () => navigate("/premium") },
    });
  };

  // Alegerea monedei — orice ≠ RON e Premium.
  const chooseCurrency = (c) => {
    if (c !== "RON" && !isPremium) {
      upsell("Cheltuielile în altă monedă (conversie în RON) sunt în Chit Premium.");
      return;
    }
    setCurrency(c);
  };

  // Data cheltuielii, formatată în română.
  const dataFormatata = expenseDate.toLocaleDateString("ro-RO", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const esteAzi = expenseDate.toDateString() === new Date().toDateString();

  // Încarcă bonul/factura (Premium).
  const handleUploadReceipt = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite re-selectarea aceluiași fișier
    if (!file) return;
    if (!isPremium) {
      upsell("Încărcarea bonului face parte din Chit Premium.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fișierul e prea mare (max 10 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/upload/receipt`, fd, {
        withCredentials: true,
      });
      setReceiptId(r.data.receiptId);
      setReceiptName(r.data.receiptName);
      toast.success("Bon atașat");
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail.startsWith("PREMIUM:")) {
        upsell(detail.replace("PREMIUM:", ""));
      } else {
        toast.error(detail || "Eroare la încărcarea bonului");
      }
    } finally {
      setUploading(false);
    }
  };

  // Șterge bonul atașat. Îl marcăm șters pe server doar dacă e un bon
  // proaspăt încărcat (nu unul deja salvat pe o cheltuială existentă).
  const removeReceipt = async () => {
    const id = receiptId;
    const eraSalvat = esteEditare && expense?.receiptId === id;
    setReceiptId(null);
    setReceiptName(null);
    if (id && !eraSalvat) {
      try {
        await axios.delete(`${API}/upload/receipt/${id}`, { withCredentials: true });
      } catch {
        // ignorăm
      }
    }
  };

  // Alegerea modului de împărțire — sume/procente sunt Premium.
  const chooseMode = (id) => {
    if (!isPremium && id !== "egal") {
      upsell("Împărțirea pe sume/procente e în Chit Premium.");
      return;
    }
    setSplitMode(id);
  };

  const toggleSelected = (id) => {
    // Gratuit: se împarte egal ÎNTRE TOȚI — nu poți deselecta membri.
    if (!isPremium && selected.includes(id)) {
      upsell("Împărțirea doar între anumiți membri e în Chit Premium.");
      return;
    }
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Calculează părțile (splits) în funcție de modul ales.
  // Întoarce { splits } dacă e ok, sau { eroare } dacă ceva nu se potrivește.
  const calculeazaSplits = () => {
    const t = Math.round(totalRON * 100) / 100;

    if (splitMode === "egal") {
      if (selected.length === 0)
        return { eroare: "Alege între cine se împarte" };
      return { splits: imparteEgal(t, selected) };
    }

    if (splitMode === "sume") {
      const rawSplits = [];
      let sumaRaw = 0;
      group.members.forEach((m) => {
        const v = parseFloat(sume[m.id]);
        if (Number.isFinite(v) && v > 0) {
          const val = Math.round(v * 100) / 100; // în moneda aleasă (RON sau valută)
          rawSplits.push({ memberId: m.id, amount: val });
          sumaRaw += val;
        }
      });
      if (rawSplits.length === 0) return { eroare: "Introdu cel puțin o sumă" };
      const totalOriginal = Math.round(total * 100) / 100;
      if (Math.abs(Math.round(sumaRaw * 100) / 100 - totalOriginal) > 0.01)
        return {
          eroare: `Sumele adunate (${sumaRaw.toFixed(2)} ${currency}) trebuie să dea ${totalOriginal.toFixed(
            2
          )} ${currency}`,
        };
      // Stocăm întotdeauna în RON (contul e ținut în RON); convertim sumele
      // introduse în valută la cursul oficial, corectând restul de rotunjire.
      let splits = rawSplits.map((s) => ({
        memberId: s.memberId,
        amount: currency === "RON" ? s.amount : Math.round(s.amount * rate * 100) / 100,
      }));
      splits = ajusteazaRest(splits, t);
      return { splits };
    }

    // splitMode === "procente"
    let totalPct = 0;
    const raw = [];
    group.members.forEach((m) => {
      const v = parseFloat(procente[m.id]);
      if (Number.isFinite(v) && v > 0) {
        raw.push({ memberId: m.id, pct: v });
        totalPct += v;
      }
    });
    if (raw.length === 0) return { eroare: "Introdu cel puțin un procent" };
    if (Math.abs(totalPct - 100) > 0.01)
      return {
        eroare: `Procentele trebuie să dea exact 100% (acum: ${totalPct}%)`,
      };
    let splits = raw.map((r) => ({
      memberId: r.memberId,
      amount: Math.round(t * r.pct) / 100, // t * pct / 100, rotunjit la bani
    }));
    splits = ajusteazaRest(splits, t); // corectăm restul de rotunjire
    return { splits };
  };

  const handleSave = async () => {
    if (!totalValid) {
      toast.error("Introdu o sumă validă (minim 0,01 RON)");
      return;
    }
    if (currency !== "RON" && !rate) {
      toast.error("Cursul valutar nu e disponibil momentan. Încearcă în RON.");
      return;
    }
    if (description.trim() === "") {
      toast.error("Adaugă o descriere");
      return;
    }
    if (!paidBy) {
      toast.error("Alege cine a plătit");
      return;
    }
    const rez = calculeazaSplits();
    if (rez.eroare) {
      toast.error(rez.eroare);
      return;
    }

    // Data aleasă: dacă e azi păstrăm ora curentă (ordine corectă),
    // altfel folosim prânzul acelei zile.
    const c = expenseDate;
    const createdAt = esteAzi
      ? new Date().toISOString()
      : new Date(c.getFullYear(), c.getMonth(), c.getDate(), 12, 0, 0).toISOString();

    const data = {
      amount: Math.round(totalRON * 100) / 100,
      description,
      paidBy,
      category,
      splitMode,
      splits: rez.splits,
      currency,
      originalAmount:
        currency === "RON" ? null : Math.round(total * 100) / 100,
      createdAt,
      receiptId,
      receiptName,
    };
    try {
      if (esteEditare) {
        await updateExpense(group.id, expense.id, data);
        toast.success("Cheltuială actualizată");
      } else {
        await addExpense(group.id, data);
        toast.success("Cheltuială adăugată");
      }
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail.startsWith("PREMIUM:")) {
        toast.error(detail.replace("PREMIUM:", ""), {
          action: { label: "Vezi Premium", onClick: () => navigate("/premium") },
        });
      } else {
        toast.error(detail || "Eroare la salvare");
      }
    }
  };

  // Sume live pentru afișarea „câți bani mai sunt de repartizat".
  const sumaIntrodusa = group.members.reduce((s, m) => {
    const v = parseFloat(sume[m.id]);
    return s + (Number.isFinite(v) && v > 0 ? v : 0);
  }, 0);
  const procentIntrodus = group.members.reduce((s, m) => {
    const v = parseFloat(procente[m.id]);
    return s + (Number.isFinite(v) && v > 0 ? v : 0);
  }, 0);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-w-md mx-auto">
        <DrawerHeader className="text-left pb-2">
          <DrawerTitle className="font-heading text-2xl font-extrabold">
            {esteEditare ? "Editează cheltuiala" : "Cheltuială nouă"}
          </DrawerTitle>
        </DrawerHeader>

        <div className="px-4 pb-4 space-y-6 overflow-y-auto max-h-[68vh]">
          {/* Suma - input mare, în centru */}
          <div className="text-center pt-2">
            <div className="flex items-baseline justify-center gap-1">
              <input
                data-testid="expense-amount-input"
                type="number"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
                className="w-40 text-6xl font-black text-center text-emerald-600 bg-transparent border-none outline-none placeholder:text-slate-200"
                autoFocus={!esteEditare}
              />
              <span className="text-2xl font-bold text-slate-400">{currency}</span>
            </div>

            {/* Selector de monedă (RON gratuit; restul — Premium) */}
            <div className="mt-3 inline-flex gap-1 bg-slate-100 rounded-2xl p-1">
              {VALUTE.map((c) => {
                const locked = c !== "RON" && !isPremium;
                return (
                  <button
                    key={c}
                    data-testid={`currency-${c}`}
                    onClick={() => chooseCurrency(c)}
                    className={`px-3 py-1.5 rounded-xl text-sm font-bold flex items-center gap-1 transition-colors ${
                      currency === c
                        ? "bg-white text-emerald-700 shadow-sm"
                        : "text-slate-500"
                    }`}
                  >
                    {locked && <Lock className="h-3 w-3" />}
                    {c}
                  </button>
                );
              })}
            </div>

            {/* Indiciu de conversie în RON (curs oficial BNR) */}
            {currency !== "RON" && (
              <p data-testid="fx-hint" className="mt-2 text-xs text-slate-500">
                {rate ? (
                  <>
                    ≈{" "}
                    <span className="font-bold text-emerald-600">
                      {totalRONValid ? formateazaRON(totalRON) : formateazaRON(0)}
                    </span>{" "}
                    · curs {fx?.source === "BNR" ? "BNR" : fx?.source || "oficial"}: 1{" "}
                    {currency} = {rate.toFixed(4)} RON
                    {fx?.date ? ` (${fx.date})` : ""}
                  </>
                ) : (
                  "Se încarcă cursul valutar..."
                )}
              </p>
            )}
          </div>

          {/* Descriere */}
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-2 block">
              Pentru ce?
            </label>
            <Input
              data-testid="expense-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex: Bere, Taxi, Cazare"
              className="h-12 rounded-xl text-base"
            />
          </div>

          {/* Categorie - chips cu iconițe */}
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-2 block">
              Categorie
            </label>
            <div className="flex flex-wrap gap-2">
              {CATEGORII.map((c) => {
                const Icon = c.icon;
                const activ = category === c.id;
                return (
                  <button
                    key={c.id}
                    data-testid={`category-chip-${c.id}`}
                    onClick={() => setCategory(c.id)}
                    className={`px-3 py-2 rounded-full text-sm font-semibold flex items-center gap-1.5 transition-colors ${
                      activ
                        ? "bg-emerald-600 text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    <Icon className="h-4 w-4" strokeWidth={2.5} />
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Data cheltuielii (disponibilă tuturor) */}
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-2 block">
              Când?
            </label>
            <button
              type="button"
              data-testid="expense-date-btn"
              onClick={() => setShowCal((v) => !v)}
              className="w-full h-12 rounded-xl border border-slate-200 px-4 flex items-center gap-2 text-sm font-medium text-slate-700 hover:border-emerald-400 transition-colors"
            >
              <CalendarIcon className="h-4 w-4 text-emerald-600" strokeWidth={2.5} />
              {esteAzi ? `Azi, ${dataFormatata}` : dataFormatata}
            </button>
            {showCal && (
              <div
                data-testid="expense-calendar"
                className="mt-2 rounded-2xl border border-slate-200 bg-white flex justify-center"
              >
                <Calendar
                  mode="single"
                  selected={expenseDate}
                  onSelect={(d) => {
                    if (d) setExpenseDate(d);
                    setShowCal(false);
                  }}
                  disabled={(d) => d > new Date()}
                />
              </div>
            )}
          </div>

          {/* Bon / factură (Premium) */}
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
              Bon / factură
              {!isPremium && <Lock className="h-3 w-3 text-amber-600" />}
            </label>
            {receiptId ? (
              <div
                data-testid="receipt-attached"
                className="w-full h-12 rounded-xl border border-emerald-200 bg-emerald-50 px-4 flex items-center gap-2 text-sm"
              >
                <Receipt className="h-4 w-4 text-emerald-600 shrink-0" strokeWidth={2.5} />
                <span className="flex-1 truncate font-medium text-emerald-800">
                  {receiptName || "Bon atașat"}
                </span>
                <button
                  data-testid="receipt-remove-btn"
                  onClick={removeReceipt}
                  aria-label="Șterge bonul"
                  className="h-7 w-7 flex items-center justify-center rounded-full hover:bg-emerald-100 text-emerald-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label
                data-testid="receipt-upload-label"
                className={`w-full h-12 rounded-xl border border-dashed px-4 flex items-center gap-2 text-sm font-medium transition-colors cursor-pointer ${
                  isPremium
                    ? "border-slate-300 text-slate-600 hover:border-emerald-400"
                    : "border-slate-200 text-slate-400"
                }`}
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
                ) : (
                  <Paperclip className="h-4 w-4" strokeWidth={2.5} />
                )}
                {uploading ? "Se încarcă..." : "Atașează bon / factură"}
                {!isPremium && <Lock className="h-3 w-3 ml-auto text-amber-600" />}
                <input
                  data-testid="receipt-upload-input"
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp,.heic,.pdf"
                  className="hidden"
                  onClick={(e) => {
                    if (!isPremium) {
                      e.preventDefault();
                      upsell("Încărcarea bonului face parte din Chit Premium.");
                    }
                  }}
                  onChange={handleUploadReceipt}
                />
              </label>
            )}
          </div>

          {/* Cine a plătit */}
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-2 block">
              Cine a plătit?
            </label>
            <div className="flex flex-wrap gap-2">
              {group.members.map((m) => (
                <button
                  key={m.id}
                  data-testid={`paidby-chip-${m.id}`}
                  onClick={() => setPaidBy(m.id)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                    paidBy === m.id
                      ? "bg-emerald-600 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {m.name}
                </button>
              ))}
            </div>
          </div>

          {/* Cum se împarte - selector de mod */}
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-2 block">
              Cum se împarte?
            </label>
            <div className="grid grid-cols-3 gap-1 bg-slate-100 rounded-2xl p-1">
              {MODURI.map((mod) => {
                const locked = !isPremium && mod.id !== "egal";
                return (
                  <button
                    key={mod.id}
                    data-testid={`split-mode-${mod.id}`}
                    onClick={() => chooseMode(mod.id)}
                    className={`py-2 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-1 ${
                      splitMode === mod.id
                        ? "bg-white text-emerald-700 shadow-sm"
                        : "text-slate-500"
                    }`}
                  >
                    {locked && <Lock className="h-3 w-3" />}
                    {mod.label}
                  </button>
                );
              })}
            </div>
            {!isPremium && (
              <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                <Lock className="h-3 w-3" />
                Împărțirea avansată (sume, procente, doar anumiți membri) e în
                Chit Premium.
              </p>
            )}

            {/* Modul EGAL: alegi membrii, se împarte egal între ei */}
            {splitMode === "egal" && (
              <>
                <div className="flex items-center justify-between mt-3 mb-2">
                  <span className="text-xs text-slate-500">Între cine?</span>
                  <button
                    data-testid="split-all-btn"
                    onClick={() =>
                      setSelected(group.members.map((m) => m.id))
                    }
                    className="text-xs font-semibold text-emerald-600 hover:text-emerald-700"
                  >
                    Toți
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {group.members.map((m) => {
                    const activ = selected.includes(m.id);
                    return (
                      <button
                        key={m.id}
                        data-testid={`split-chip-${m.id}`}
                        onClick={() => toggleSelected(m.id)}
                        className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors flex items-center gap-1 ${
                          activ
                            ? "bg-emerald-100 text-emerald-700 ring-2 ring-emerald-500"
                            : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                        }`}
                      >
                        {activ && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                        {m.name}
                      </button>
                    );
                  })}
                </div>
                {selected.length > 0 && totalRONValid && (
                  <p className="text-xs text-slate-500 mt-2">
                    Câte {(totalRON / selected.length).toFixed(2)} RON de persoană
                  </p>
                )}
              </>
            )}

            {/* Modul SUME: introduci suma exactă pentru fiecare, în moneda aleasă mai sus */}
            {splitMode === "sume" && (
              <div className="mt-3 space-y-2">
                {currency !== "RON" && (
                  <p data-testid="sume-currency-hint" className="text-xs text-slate-400 -mt-1 mb-1">
                    Introduci sumele în {currency} — se convertesc automat în RON la curs oficial.
                  </p>
                )}
                {group.members.map((m) => (
                  <div key={m.id} className="flex items-center gap-3">
                    <span className="flex-1 text-sm font-medium text-slate-700">
                      {m.name}
                    </span>
                    <input
                      data-testid={`sume-input-${m.id}`}
                      type="number"
                      inputMode="decimal"
                      value={sume[m.id] || ""}
                      onChange={(e) =>
                        setSume({ ...sume, [m.id]: e.target.value })
                      }
                      placeholder="0"
                      className="w-24 h-10 rounded-xl border border-slate-200 px-3 text-right text-sm outline-none focus:border-emerald-500"
                    />
                    <span className="text-xs text-slate-400 w-8">{currency}</span>
                  </div>
                ))}
                <p
                  className={`text-xs mt-1 ${
                    totalValid && Math.abs(sumaIntrodusa - total) < 0.01
                      ? "text-emerald-600"
                      : "text-slate-500"
                  }`}
                >
                  Repartizat: {sumaIntrodusa.toFixed(2)} din{" "}
                  {totalValid ? total.toFixed(2) : "0.00"} {currency}
                </p>
              </div>
            )}

            {/* Modul PROCENTE: introduci procentul pentru fiecare */}
            {splitMode === "procente" && (
              <div className="mt-3 space-y-2">
                {group.members.map((m) => {
                  const pct = parseFloat(procente[m.id]);
                  const suma =
                    totalRONValid && Number.isFinite(pct) && pct > 0
                      ? ((totalRON * pct) / 100).toFixed(2)
                      : null;
                  return (
                    <div key={m.id} className="flex items-center gap-3">
                      <span className="flex-1 text-sm font-medium text-slate-700">
                        {m.name}
                      </span>
                      {suma && (
                        <span className="text-xs text-slate-400">{suma} RON</span>
                      )}
                      <input
                        data-testid={`procente-input-${m.id}`}
                        type="number"
                        inputMode="decimal"
                        value={procente[m.id] || ""}
                        onChange={(e) =>
                          setProcente({ ...procente, [m.id]: e.target.value })
                        }
                        placeholder="0"
                        className="w-20 h-10 rounded-xl border border-slate-200 px-3 text-right text-sm outline-none focus:border-emerald-500"
                      />
                      <span className="text-xs text-slate-400 w-4">%</span>
                    </div>
                  );
                })}
                <p
                  className={`text-xs mt-1 ${
                    Math.abs(procentIntrodus - 100) < 0.01
                      ? "text-emerald-600"
                      : "text-slate-500"
                  }`}
                >
                  Total: {Math.round(procentIntrodus)}% din 100%
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-slate-100">
          <Button
            data-testid="save-expense-btn"
            onClick={handleSave}
            className="w-full py-6 rounded-2xl text-base font-bold bg-emerald-600 hover:bg-emerald-700 active:scale-95 transition-transform"
          >
            {esteEditare ? "Salvează modificările" : "Adaugă cheltuiala"}
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
